import { useMemo } from "react";
import { useQuery } from "react-query";

import axios from "axios";

import { REACT_APP_STICK_SCHEDULER_SERVICE_ADDRESS } from "../modals/constants";
import { StickCalculationsJob } from "../models/StickCalculations";
import { isInProgressStatus } from "../utils/jobStatusTaxonomy";

export async function geoSticksCalculationJobs() {
  // A failed request throws: an empty list would read as "this org has no jobs".
  const response = await axios.get<StickCalculationsJob[]>(
    `${REACT_APP_STICK_SCHEDULER_SERVICE_ADDRESS}/job`
  );

  if (!Array.isArray(response.data)) {
    // API returns 200 with an empty array if there are no jobs
    return [];
  }

  return response.data;
}

export const STICK_JOBS_POLL_MS = 3000;

// The stick worker writes live progress into each job's status ("Started", then
// "40/128", …). This list is the sim table's only status source, so poll it
// while any job is in progress and stop once all have settled — giving the sim
// table the same live updates as the calc table.
export function stickJobsRefetchInterval(data?: StickCalculationsJob[]): number | false {
  return (data ?? []).some((job) => isInProgressStatus(job.status))
    ? STICK_JOBS_POLL_MS
    : false;
}

// `enabled` and `poll` are for callers outside the settings pages: this list is
// the whole org's jobs, so a surface that renders for every user has to be able
// to ask for it only when it needs it, and a caller that reads the answer once
// has no use for the live progress poll.
export function useGeoSticksCalculationJobs(
  scenarioIds?: string[],
  options?: { enabled?: boolean; poll?: boolean }
) {
  const poll = options?.poll ?? true;
  const query = useQuery<StickCalculationsJob[]>(
    "stickCalculationJobs",
    geoSticksCalculationJobs,
    {
      enabled: options?.enabled,
      refetchInterval: (data) => (poll ? stickJobsRefetchInterval(data) : false)
    }
  );

  const filteredData = useMemo(() => {
    if (!query.data || !scenarioIds?.length) return query.data;

    const allowedSet = new Set(scenarioIds);
    return query.data.filter(
      (job) => !job.savedFilterId || job.scenarioIds?.some((id) => allowedSet.has(id))
    );
  }, [query.data, scenarioIds]);

  return { ...query, data: filteredData };
}
